/**
 * The one call every create point in the UI makes before creating something
 * locally (Plans v2 card 3.2):
 *
 *   if (!requireLocalCapacity('boards', boards.length)) return null;
 *   if (!(await requireLocalCapacityFor('boards', countBoards))) return null;
 *
 * Refused → the upgrade modal opens with "1 / 1 boards on Free: Basic removes
 * the limit" (card 3.3: a locked create opens the upgrade, never a dead
 * button). Nothing that already exists is touched: data over the cap stays
 * readable, editable and exportable; only creating more is refused.
 *
 * The AI and MCP paths refuse with a sentence instead: plans/capRefusal.js.
 */
import { checkLocalLimit } from '../store/entitlementsStore';
import { setUpgradeModalOpen, setUpgradeReason } from '../store/uiModalStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { localLimitMessage, capRefusal, isCapped, readOnlyWorkspaceRefusal } from './capRefusal';
import { hasCloudSync } from './cloudAccess';
import i18n from '../i18n';

export { localLimitMessage, capRefusal };

/**
 * True when `opts.adding` (default 1) more `kind` may be created next to
 * `used` existing ones. False after opening the upgrade modal. `opts.silent`
 * only checks.
 */
export function requireLocalCapacity(kind, used, opts = {}) {
  const outcome = checkLocalLimit(kind, used, opts.adding);
  if (outcome.ok) return true;
  if (!opts.silent) {
    setUpgradeReason(localLimitMessage(outcome));
    setUpgradeModalOpen(true);
  }
  return false;
}

/**
 * Card 3.4: may this account put an item in the cloud? True with
 * `cloud_sync`; otherwise the upgrade opens (card 3.3) and it is false.
 */
export function requireCloudSync() {
  if (hasCloudSync()) return true;
  setUpgradeReason(i18n.t('cloudLocal.upgradeForCloud'));
  setUpgradeModalOpen(true);
  return false;
}

/**
 * May the user write to this API workspace (default: the open one)? False —
 * with the upgrade open and the read-only sentence — for an API workspace of
 * the account's own cloud while that cloud is read-only (Free/Basic: the
 * server refuses every write there with 402 `read_only`). Local and team
 * workspaces, and a cloud with `cloud_sync`, are always writable here.
 */
export function requireWritableWorkspace(workspace = useWorkspaceStore.getState().currentWorkspace) {
  const refused = readOnlyWorkspaceRefusal(workspace);
  if (!refused) return true;
  setUpgradeReason(refused);
  setUpgradeModalOpen(true);
  return false;
}

/** Same, counting (async `countFn`) only when the plan caps `kind`. */
export async function requireLocalCapacityFor(kind, countFn, opts = {}) {
  if (!isCapped(kind)) return true;
  return requireLocalCapacity(kind, await countFn(), opts);
}
